import {
  combineReducers,
  configureStore,
  Reducer,
  Store,
  ThunkDispatch,
} from '@reduxjs/toolkit'
import { emptySplitApi as portalApi } from 'api/emptyApi'
import bevarSlice from 'Modules/Boardwork/Bevar/shared/store/bevarSlice'
import bblArchiveSlice from 'Modules/Boardwork/DocumentArchive/BblArchive/BblArchiveSlice'
import boardArchiveSlice from 'Modules/Boardwork/DocumentArchive/BoardArchive/BoardArchiveSlice'
import boardUpdateListSlice from 'Modules/Boardwork/DocumentArchive/BoardArchive/UpdateFileList/BoardUpdateListSlice'
import documentArchiveSlice from 'Modules/Boardwork/DocumentArchive/documentArchiveSlice'
import prosjektArkivSlice from 'Modules/Boardwork/DocumentArchive/ProsjektArkiv/ProsjektArkivSlice'
import uploadFilesSlice from 'Modules/Boardwork/DocumentArchive/UploadFIles/store/UploadFilesSlice'
import taskReducer from 'Modules/Boardwork/Tasks/shared/store'
import bankAccountsSlice from 'Modules/Economy/BankAccounts/store/BankAccountsSlice'
import budgetSlice from 'Modules/Economy/Budget/shared/store/budgetSlice'
import invoiceSlice from 'Modules/Economy/Invoice/shared/store/invoice.slice'
import loanSlice from 'Modules/Economy/Loan/shared/store/loanSlice'
import reimbursementSlice from 'Modules/Economy/Reimbursement/shared/reimbursementSlice'
import balanceAndTransactionsSlice from 'Modules/Economy/shared/balanceAndTransactionSlice'
import publicationSlice from 'Modules/HousingCooperative/Publications/shared/store/publicationSlice'
// eslint-disable-next-line no-restricted-imports
import { useDispatch, useSelector } from 'react-redux'
import { UnknownAction } from 'redux'
import createSagaMiddleware, { Saga } from 'redux-saga'
import { BoardReducer } from './board'
import clientsReducer from './clients'
import clientsApi from './clients/clients.service'
import CommunicationReducer from './communication'
import customtextApi from './customtexts/customtexts.service'
import economyApi from './economy/economy.service'
import { EmailSettingsReducer } from './emailSettings'
import { emptySplitApi } from './emptyApi'
import guidesUiReducer from './guidesUi/slice'
import { KeyFiguresReducer } from './keyFigures'
import menuReducer from './menu'
import menuApi from './menu/menu.service'
import modalReducer from './modal'
import { reducer as NotificationsReducer } from './notifications'
import { createSagaInjector, rootSaga } from './sagas'
import SendMessageReducer from './sendMessage'
import { SocketReducer } from './socket'
import terminologyApi from './terminology/terminology.service'
import { reducer as ToasterReducer } from './toaster'
import userApi from './user/user.service'

/**
 * Store interface
 */
export interface PortalStore extends Store {
  injectReducer: (key: string, asyncReducer: Reducer) => void
  asyncReducers: Record<string, Reducer>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  injectSaga: (key: string, saga: Saga<any[]>) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inject: (key: string, asyncReducer: Reducer, saga: Saga<any[]>) => void
  destroyReducer: (key: string) => void
}

// emptySplitApi now lives in ./emptyApi to break the store <-> slice circular
// import. Re-exported here so existing `from 'shared/store/store'` imports that
// still reference it keep working.
export { emptySplitApi } from './emptyApi'

/**
 * Static reducers
 * Should exists over the whole application
 */
export let reducers = {
  board: BoardReducer,
  keyFigures: KeyFiguresReducer,
  guidesUi: guidesUiReducer,
  toaster: ToasterReducer,
  notifications: NotificationsReducer,
  socket: SocketReducer,
  sendMessage: SendMessageReducer,
  emailSettings: EmailSettingsReducer,
  communication: CommunicationReducer,
  tasks: taskReducer,
  // Slices
  [modalReducer.name]: modalReducer.reducer,
  [menuReducer.name]: menuReducer.reducer,
  [clientsReducer.name]: clientsReducer.reducer,
  [publicationSlice.name]: publicationSlice.reducer,
  [loanSlice.name]: loanSlice.reducer,
  [budgetSlice.name]: budgetSlice.reducer,
  [bevarSlice.name]: bevarSlice.reducer,
  [reimbursementSlice.name]: reimbursementSlice.reducer,
  [bankAccountsSlice.name]: bankAccountsSlice.reducer,
  [balanceAndTransactionsSlice.name]: balanceAndTransactionsSlice.reducer,
  [bblArchiveSlice.name]: bblArchiveSlice.reducer,
  [boardArchiveSlice.name]: boardArchiveSlice.reducer,
  [documentArchiveSlice.name]: documentArchiveSlice.reducer,
  [prosjektArkivSlice.name]: prosjektArkivSlice.reducer,
  [boardUpdateListSlice.name]: boardUpdateListSlice.reducer,
  [uploadFilesSlice.name]: uploadFilesSlice.reducer,
  [invoiceSlice.name]: invoiceSlice.reducer,
  // APIs
  [clientsApi.reducerPath]: clientsApi.reducer,
  [economyApi.reducerPath]: economyApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [terminologyApi.reducerPath]: terminologyApi.reducer,
  [menuApi.reducerPath]: menuApi.reducer,
  [customtextApi.reducerPath]: customtextApi.reducer,
  [emptySplitApi.reducerPath]: emptySplitApi.reducer,
  [portalApi.reducerPath]: portalApi.reducer,
}

export const sagaMiddleware = createSagaMiddleware()
export const reduxMiddleware = [
  menuApi.middleware,
  clientsApi.middleware,
  economyApi.middleware,
  userApi.middleware,
  customtextApi.middleware,
  terminologyApi.middleware,
  emptySplitApi.middleware,
  portalApi.middleware,
]

export const store = configureStore({
  reducer: combineReducers(reducers),
  // @ts-expect-error: old code
  middleware: (getDefaultMiddleware) => [
    ...getDefaultMiddleware({ serializableCheck: false }).concat(
      reduxMiddleware
    ),
    sagaMiddleware,
  ],
  devTools: process.env.NODE_ENV !== 'production',
})

/**
 * Init store
 */

export const registerReducer = (key: string, newReducer: Reducer): Store => {
  reducers = { ...reducers, [key]: newReducer }
  // @ts-expect-error: old code
  store.replaceReducer(combineReducers(reducers))
  // @ts-expect-error: old code
  return store
}

export const injectSaga = createSagaInjector(sagaMiddleware.run, rootSaga)
export const injectReducer = registerReducer
export const inject = (
  key: string,
  asyncReducer?: Reducer,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  saga?: Saga<any[]>
) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  asyncReducer && injectReducer(key, asyncReducer)
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  saga && injectSaga(key, saga)
}

export type RootState = ReturnType<typeof store.getState>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AppDispatch = ThunkDispatch<RootState, any, UnknownAction>

export const useAppDispatch = useDispatch.withTypes<AppDispatch>()

/**
 * Typed selector hook. The first overload enforces RootState for statically
 * registered slices. The second overload handles selectors for code-split
 * (dynamically-injected) slices whose types are not in the static RootState.
 */
export function useAppSelector<TSelected>(
  selector: (state: RootState) => TSelected
): TSelected
export function useAppSelector<TState, TSelected>(
  selector: (state: TState) => TSelected
): TSelected
export function useAppSelector<TState, TSelected>(
  selector: (state: TState) => TSelected
): TSelected {
  return useSelector(selector as unknown as (state: RootState) => TSelected)
}

export default store
